<template>
  <v-card elevation="1" class="container-card">
    <v-row align="center" justify="start" class="top-bar pa-3">
      <v-col cols="12">
        <div class="d-flex justify-start align-center">
          <v-icon class="mr-2" icon="add_shopping_cart" />
          <div class="md-light">Select Store Product</div>
          <v-spacer />
          <v-progress-circular
            v-if="loading"
            :indeterminate="true"
            size="25"
            width="2"
            class="loading-wheel"
          />
        </div>
      </v-col>
    </v-row>
    <v-col cols="12">
      <v-divider />
    </v-col>
    <v-form class="pa-3">
      <v-select
        label="Product Name"
        v-model="selectedProduct"
        item-title="product_name"
        item-value="product_name"
        :items="products"
        @update:modelValue="populatePrimaryAttributes"
        data-testid="add-order-product-name"
        :disabled="disabled"
      >
      </v-select>
      <v-select
        :label="primaryAttributeName"
        :items="primaryAttributes"
        v-model="selectedPrimaryAttribute"
        @update:modelValue="populateSecondaryAttributes"
        no-data-text="Select a product"
        :disabled="disabled"
        data-testid="add-order-primary-attribute"
      >
      </v-select>
      <v-select
        :label="secondaryAttributeName"
        :items="secondaryAttributes"
        v-model="selectedSecondaryAttribute"
        :no-data-text="secondaryAttributeNoDataText"
        :disabled="disabled"
        data-testid="add-order-secondary-attribute"
      >
      </v-select>
      <v-text-field
        type="number"
        min="1"
        v-model="quantity"
        :rules="[quantity > 0 || 'Must be at least 1']"
        label="Quantity"
        :disabled="disabled"
        data-testid="add-order-quantity"
      >
      </v-text-field>
      <div class="d-flex align-center">
        <v-btn
          class="mt-2"
          variant="flat"
          :disabled="!canAddOrderItem || disabled"
          @click="addOrderItem"
          data-testid="add-order-add-to-cart"
        >
          Add to Cart
        </v-btn>
        <div class="d-flex align-center" v-if="selectedProductHasLowStock">
          <i class="text-primary"> Out of stock </i>
          <v-tooltip location="right" class="pr-2">
            <template v-slot:activator="{ props }">
              <v-btn icon variant="plain" v-bind="props">
                <v-icon color="error" icon="error" />
              </v-btn>
            </template>
            <span> Product is low on stock </span>
          </v-tooltip>
        </div>
      </div>
    </v-form>
  </v-card>
</template>

<script>
import store from "@/store";

import helpers from "@/mixins/helpers";
import { cartStoreArgs } from "@/router/storefrontStoreId";

export default {
  components: {},
  mixins: [helpers],

  props: {
    storeID: {
      type: Number,
      required: true,
    },
    campaign: {
      type: Object,
      required: true,
    },
    width: {
      type: Number,
      required: false,
      default: 550,
    },
    disabled: {
      type: Boolean,
      required: false,
      default: false,
    },
  },

  data() {
    return {
      selectedProduct: null,
      products: [],

      primaryAttributes: [],
      primaryAttributeName: null,
      selectedPrimaryAttribute: null,

      secondaryAttributes: [],
      secondaryAttributeName: null,
      selectedSecondaryAttribute: null,
      secondaryAttributeNoDataText: "",

      quantity: 1,

      orderItems: null,
      stockInfo: null,

      loading: false,
      displayCartLimitError: true,
    };
  },

  computed: {
    selectedProductHasLowStock() {
      if (this.selectedProduct);
      if (!this.stockInfo) {
        return false;
      } else {
        for (let i = 0; i < this.stockInfo.length; i++) {
          let productVariants = this.stockInfo[i];
          for (let j = 0; j < productVariants.length; j++) {
            let variant = productVariants[j];
            if (
              variant["color"] != this.selectedPrimaryAttribute ||
              variant["product_name"] != this.selectedProduct
            ) {
              continue;
            }
            let hasStockAlert =
              variant["low_stock_alert"][this.selectedSecondaryAttribute];
            return hasStockAlert;
          }
        }
      }
    },

    canAddOrderItem() {
      return (
        this.selectedProduct &&
        this.selectedPrimaryAttribute &&
        this.selectedSecondaryAttribute &&
        this.quantity > 0
      );
    },
  },

  watch: {},

  mounted() {
    this.load();
  },

  methods: {
    async load() {
      //this.loadStore();
      this.loading = true;
      await this.loadProducts();
      this.loading = false;
    },

    async loadProducts() {
      let result;
      try {
        result = await this.$axios.get(
          `/product/getCampaignProductList/${this.storeID}`
        );
      } catch (err) {
        this.$store.dispatch('raiseError', err);
      } finally {
        this.products = result.data;

        this.primaryAttributeName = this.products[0].primary_attribute_name;
        this.secondaryAttributeName = this.products[0].secondary_attribute_name;
        this.secondaryAttributeNoDataText =
          "Select a " + this.primaryAttributeName;
      }
    },

    populatePrimaryAttributes() {
      //clear attribute lists and subsequent selected information
      this.primaryAttributes = [];
      this.secondaryAttributes = [];
      this.selectedPrimaryAttribute = null;
      this.selectedSecondaryAttribute = null;

      let productIndex = 0;
      //populate primaryAttributes
      for (let i = 0; i < this.products.length; i++) {
        let product = this.products[i];
        if (product.product_name == this.selectedProduct) {
          productIndex = i;
          break;
        }
      }
      let productAttributes = this.products[productIndex].attributes;

      //populate primary attribute name
      this.primaryAttributeName =
        this.products[productIndex].primary_attribute_name;
      this.secondaryAttributeNoDataText =
        "Select a " + this.primaryAttributeName;
      //populate secondary attribute name
      this.secondaryAttributeName =
        this.products[productIndex].secondary_attribute_name;

      for (const primaryAttribute in productAttributes) {
        if (Object.hasOwnProperty.call(productAttributes, primaryAttribute)) {
          this.primaryAttributes.push(primaryAttribute);
        }
      }
    },

    populateSecondaryAttributes() {
      //clear attribute lists and subsequent selected information
      this.secondaryAttributes = [];
      this.selectedSecondaryAttribute = null;

      //populate secondaryAttributes
      let productIndex = 0;

      for (let i = 0; i < this.products.length; i++) {
        let product = this.products[i];
        if (product.product_name == this.selectedProduct) {
          productIndex = i;
          break;
        }
      }
      let secondaryAttributes =
        this.products[productIndex].attributes[this.selectedPrimaryAttribute];

      for (const attribute in secondaryAttributes) {
        if (Object.hasOwnProperty.call(secondaryAttributes, attribute)) {
          this.secondaryAttributes.push(secondaryAttributes[attribute]);
        }
      }
    },

    async addOrderItem() {
      let orderItem = this.generateOrderItem();
      let selectedProduct;

      for (let i = 0; i < this.products.length; i++) {
        let product = this.products[i];
        if (product.product_name == this.selectedProduct) {
          selectedProduct = product;
          break;
        }
      }
      let response;
      try {
        response = await this.$axios.post(
          `/accounts/${this.$store.state.account._jv.id}/update_cart_quantity`,
          {
            meta: {
              method: "update_cart_quantity",
              args: {
                ...cartStoreArgs(this.storeID),
                campaign_product_id: selectedProduct.id,
                primary_attribute_value: this.selectedPrimaryAttribute,
                secondary_attribute_value: this.selectedSecondaryAttribute,
                quantity: parseInt(this.quantity),
              },
            },
          }
        );
        if (response.data.meta.result.message) {
          if (this.displayCartLimitError) {
            this.$store.dispatch(
              "raiseError",
              response.data.meta.result.message
            );
            this.displayCartLimitError = false;
          }
        } else {
          this.displayCartLimitError = true;
        }
      } catch (err) {
        this.$store.dispatch('raiseError', err);
      } finally {
        if (response) {
          this.$store.commit("setCart", response.data.meta.result.cart);
        }
      }
    },

    generateOrderItem() {
      return null;
    },

    getStockInfo() {},
  },
};
</script>

<style scoped>
.container-card {
  width: 100%;
  height: 100%;
  border-radius: 5px;
}

.top-bar {
  background: white;
}
</style>

